import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, 
  CheckCircle, 
  ChevronLeft, 
  ChevronRight, 
  Target, 
  BookOpen,
  Brain,
  Trophy,
  RefreshCw
} from 'lucide-react';
import { examApi } from '../services/examApi';
import { authApi } from '../services/api';
import type { ChatMode } from '../services/api';
import { analytics } from '../services/analytics';
import StudyMaterialRenderer from './StudyMaterialRenderer';
import { Card, CardHeader, CardTitle, CardContent, SpaceCard } from './ui/Card';
import { PageContainer } from './layout/PageContainer';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { MixedText } from '../utils/rtl';
import ErrorMessage from './ui/ErrorMessage';
import ChatWidget from './study-session/ChatWidget';
import { StudyGuideResponse, StudyGuideTopic, StudyGuideQuestion } from '../types/exam.types';

const ExamStudyGuideView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const { courseId, examId } = useParams<{ courseId: string; examId?: string }>();
  const navigate = useNavigate();

  // Determine if this is a course-level or exam-level study guide
  const isCourseLevel = !examId;

  // State management combining MaterialsStep + ExamPracticeView patterns
  const [studyGuide, setStudyGuide] = useState<StudyGuideResponse | null>(null);
  const [activeTopicTab, setActiveTopicTab] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSolutions, setShowSolutions] = useState<{[questionId: string]: boolean}>({});
  const [exam, setExam] = useState<any>(null);

  // Chat states
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState<Array<{type: 'user' | 'ai', message: string}>>([]);
  const [messageFeedback, setMessageFeedback] = useState<{[messageIndex: number]: 'like' | 'dislike' | null}>({});
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [shouldUnminimizeChat, setShouldUnminimizeChat] = useState(false);

  // Load study guide
  useEffect(() => {
    if (courseId) {
      loadStudyGuide();
    }
  }, [courseId, examId]);

  const loadStudyGuide = async () => {
    try {
      console.log('🚀 Starting loadStudyGuide for course:', courseId);
      setLoading(true);
      setError(null);

      // Get exam details if this is exam-level
      if (!isCourseLevel) {
        const examData = await examApi.getExam(parseInt(examId!));
        setExam(examData);
      }

      if (isCourseLevel) {
        console.log('📚 Course-level study guide requested');
        // For course-level, first check if there's an existing study guide
        try {
          const statusResponse = await examApi.getCourseStudyGuideStatus(parseInt(courseId!));
          console.log('📊 Initial status check:', statusResponse);
          
          if (statusResponse.status === 'completed' && statusResponse.topics) {
            console.log('✅ Found completed study guide with', statusResponse.topics.length, 'topics');
            // Study guide is completed, display it
            setStudyGuide({ topics: statusResponse.topics });
            if (statusResponse.topics.length > 0) {
              setActiveTopicTab(statusResponse.topics[0].id);
            }
            setLoading(false);
            setGenerating(false);
            
            // Track study guide access
            analytics.track('Study Guide Accessed', {
              course_id: parseInt(courseId!),
              num_topics: statusResponse.topics.length,
              guide_type: 'course'
            });
            return;
          } else if (statusResponse.status === 'processing') {
            console.log('⏳ Study guide is processing, starting polling...');
            // Study guide is being generated, show loading and poll for updates
            setGenerating(true);
            pollForStudyGuideCompletion();
            return;
          } else if (statusResponse.status === 'failed') {
            console.log('❌ Study guide generation previously failed:', statusResponse.message);
            setError(statusResponse.message || 'Study guide generation failed');
            setGenerating(false);
            setLoading(false);
            return;
          }
          console.log('🆕 No existing study guide found, will generate new one');
          // If not_found, proceed to generate new one
        } catch (statusError) {
          console.log('🔍 No existing study guide found (error), will generate new one:', statusError);
        }
        
        // Generate new study guide
        try {
          setGenerating(true);
          await examApi.generateCourseStudyGuide(parseInt(courseId!));
          // This will return 202 and start background processing
          pollForStudyGuideCompletion();
        } catch (generateError: any) {
          if (generateError.message.includes('202') || generateError.message.includes('generation started')) {
            // Successfully started generation, begin polling
            pollForStudyGuideCompletion();
          } else {
            throw generateError;
          }
        }
      } else {
        // For exam-level, use the original logic
        setGenerating(true);
        const response = await examApi.generateStudyGuide(parseInt(examId!));
        setStudyGuide(response);
        
        // Set first topic as active
        if (response.topics && response.topics.length > 0) {
          setActiveTopicTab(response.topics[0].id);
        }

        // Track study guide access
        analytics.track('Study Guide Accessed', {
          course_id: parseInt(courseId!),
          exam_id: parseInt(examId!),
          num_topics: response.topics?.length || 0,
          guide_type: 'exam'
        });
      }

    } catch (error: any) {
      console.error('Error loading study guide:', error);
      
      // Check if it's a credit error (402)
      if (error.response?.status === 402 && error.response?.data) {
        // The credit error will be handled by the credit error interceptor
        // Just set a generic error message here
        setError(t('exams:studyGuide.errors.insufficientCredits', 'Insufficient credits to generate study guide'));
      } else {
        setError(error.message || t('exams:studyGuide.errors.loadFailed', 'Failed to load study guide'));
      }
      
      setGenerating(false);
      setLoading(false);
    } finally {
      if (!isCourseLevel) {
        setLoading(false);
        setGenerating(false);
      }
    }
  };

  const pollForStudyGuideCompletion = async () => {
    let pollCount = 0;
    const maxPolls = 100; // 5 minutes at 3-second intervals
    
    console.log('🔄 Starting study guide polling...');
    
    const pollInterval = setInterval(async () => {
      pollCount++;
      console.log(`📊 Poll attempt ${pollCount}/${maxPolls} for course ${courseId}`);
      
      try {
        const statusResponse = await examApi.getCourseStudyGuideStatus(parseInt(courseId!));
        console.log('✅ Status response:', statusResponse);
        
        if (statusResponse.status === 'completed' && statusResponse.topics) {
          console.log('🎉 Study guide completed! Topics:', statusResponse.topics.length);
          clearInterval(pollInterval);
          setStudyGuide({ topics: statusResponse.topics });
          if (statusResponse.topics.length > 0) {
            setActiveTopicTab(statusResponse.topics[0].id);
          }
          setGenerating(false);
          setLoading(false);
          
          // Track study guide access
          analytics.track('Study Guide Accessed', {
            course_id: parseInt(courseId!),
            num_topics: statusResponse.topics.length,
            guide_type: 'course'
          });
        } else if (statusResponse.status === 'failed') {
          console.log('❌ Study guide generation failed:', statusResponse.message);
          clearInterval(pollInterval);
          setError(statusResponse.message || 'Study guide generation failed');
          setGenerating(false);
          setLoading(false);
        } else if (pollCount >= maxPolls) {
          console.log('⏰ Max polls reached, stopping...');
          // Stop polling after max attempts
          clearInterval(pollInterval);
          setError('Study guide generation is taking longer than expected. Please try again later.');
          setGenerating(false);
          setLoading(false);
        } else {
          console.log(`⏳ Still processing... Status: ${statusResponse.status}`);
        }
        // Continue polling if still processing and under max polls
      } catch (pollError: any) {
        console.error('❌ Error polling for study guide status:', pollError);
        
        // If it's a server error (500) or the study guide generation failed, stop polling
        if (pollError.message?.includes('500') || pollError.message?.includes('generation failed')) {
          console.log('🛑 Server error detected, stopping polling');
          clearInterval(pollInterval);
          setError(pollError.message || 'Study guide generation failed');
          setGenerating(false);
          setLoading(false);
        } else if (pollCount >= maxPolls) {
          console.log('🛑 Max polls reached on error, stopping...');
          // Stop polling after max attempts even on network errors
          clearInterval(pollInterval);
          setError('Unable to check study guide status. Please refresh the page.');
          setGenerating(false);
          setLoading(false);
        } else {
          console.log('🔄 Network error, continuing to poll...');
        }
        // Continue polling for other errors (network issues, etc.)
      }
    }, 3000); // Poll every 3 seconds
  };

  // Current topic
  const currentTopic = studyGuide?.topics.find(t => t.id === activeTopicTab);

  // Solution visibility toggle
  const toggleSolution = useCallback((questionId: string) => {
    setShowSolutions(prev => ({ ...prev, [questionId]: !prev[questionId] }));

    // Track solution view
    const trackingData: any = {
      course_id: parseInt(courseId!),
      topic_id: activeTopicTab,
      question_id: questionId,
      action: showSolutions[questionId] ? 'hide_solution' : 'show_solution',
      guide_type: isCourseLevel ? 'course' : 'exam'
    };
    
    if (!isCourseLevel) {
      trackingData.exam_id = parseInt(examId!);
    }
    
    analytics.track('Study Guide Solution Toggled', trackingData);
  }, [showSolutions, activeTopicTab, courseId, examId, isCourseLevel]);

  const switchTopic = useCallback((topicId: string) => {
    setActiveTopicTab(topicId);
    // Don't clear solution states when switching topics - keep progress
    
    // Track topic switch
    const trackingData: any = {
      course_id: parseInt(courseId!),
      from_topic: activeTopicTab,
      to_topic: topicId,
      guide_type: isCourseLevel ? 'course' : 'exam'
    };
    
    if (!isCourseLevel) {
      trackingData.exam_id = parseInt(examId!);
    }
    
    analytics.track('Study Guide Topic Switched', trackingData);
  }, [activeTopicTab, courseId, examId, isCourseLevel]);

  // Chat handlers
  const handleChatToggle = async () => {
    if (!isChatOpen && courseId) {
      // Opening chat - load history first
      try {
        const historyResponse = await examApi.getStudyGuideChatHistory(parseInt(courseId!));
        if (historyResponse.history && historyResponse.history.length > 0) {
          setChatHistory(historyResponse.history);
        }
      } catch (error) {
        console.error('Failed to load study guide chat history:', error);
        // Don't throw error, just proceed with empty chat
      }
    }
    
    // If chat is already open and minimized, unminimize it
    if (isChatOpen && isChatMinimized) {
      setShouldUnminimizeChat(true);
      // Reset the flag after a brief delay
      setTimeout(() => setShouldUnminimizeChat(false), 100);
    } else {
      setIsChatOpen(!isChatOpen);
    }
  };

  const handleChatUpdate = (message: {type: 'user' | 'ai', message: string}) => {
    setChatHistory(prev => [...prev, message]);
  };

  // Handle message feedback
  const handleMessageFeedback = (messageIndex: number, feedback: 'like' | 'dislike') => {
    const aiMessage = chatHistory[messageIndex];
    if (aiMessage?.type !== 'ai') return;
    
    // Find the corresponding user message (previous message)
    const userMessage = messageIndex > 0 ? chatHistory[messageIndex - 1] : null;
    const userMessageContent = userMessage?.type === 'user' ? userMessage.message : '';
    
    // Update local state
    setMessageFeedback(prev => ({
      ...prev,
      [messageIndex]: prev[messageIndex] === feedback ? null : feedback
    }));
    
    // Track the feedback
    const trackingData: any = {
      course_id: parseInt(courseId!),
      message_index: messageIndex,
      feedback_type: feedback,
      ai_message: aiMessage.message,
      user_message: userMessageContent,
      guide_type: isCourseLevel ? 'course' : 'exam'
    };
    
    if (!isCourseLevel) {
      trackingData.exam_id = parseInt(examId!);
    }
    
    if (feedback === 'like') {
      analytics.track('Study Guide Chat Message Liked', trackingData);
    } else {
      analytics.track('Study Guide Chat Message Disliked', trackingData);
    }
  };

  // Custom chat handler for study guide
  const studyGuideChatHandler = async (message: string, _mode?: ChatMode) => {
    try {
      const response = await examApi.chatWithStudyGuide(
        parseInt(courseId!),
        message,
        activeTopicTab
      );
      return { response: response.response };
    } catch (error) {
      console.error('Study guide chat error:', error);
      throw error;
    }
  };

  // Show loading state
  if (loading || generating) {
    return (
      <PageContainer maxWidth="2xl" spacing="normal" backgroundIntensity="none" showParticles={false}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <SpaceCard constellation="orion" className="group hover:shadow-xl transition-all duration-300 max-w-md mx-auto">
            <CardContent className="text-center space-y-4 py-8">
              <div className="w-16 h-16 mx-auto">
                <img 
                  src="/astronaut_cool.png" 
                  alt="Generating..." 
                  className="w-full h-full object-contain animate-bounce"
                />
              </div>
              <div>
                <CardTitle className="text-xl font-bold mb-2 font-hebrew text-center">
                  <MixedText>
                    {generating 
                      ? t('exams:studyGuide.generating', 'Generating study guide...') 
                      : t('exams:studyGuide.loading', 'Loading study guide...')
                    }
                  </MixedText>
                </CardTitle>
                <p className="text-surface-secondary font-hebrew mb-4 text-center">
                  <MixedText>
                    {generating 
                      ? t('exams:studyGuide.generatingDescription', 'Analyzing exam content and creating personalized study materials')
                      : t('exams:studyGuide.loadingDescription', 'Please wait while we prepare your study guide')
                    }
                  </MixedText>
                </p>
                <p className="text-surface-muted font-hebrew text-sm text-center">
                  <MixedText>
                    {generating 
                      ? t('exams:studyGuide.pleaseWait', 'This may take a few moments...')
                      : t('exams:studyGuide.loadingWait', 'Please wait...')
                    }
                  </MixedText>
                </p>
              </div>
              <div className="flex justify-center pt-4">
                <div className="flex items-center justify-center px-6 py-3 bg-muted-purple/10 border border-muted-purple/30 rounded-lg">
                  <LoadingSpinner 
                    variant="satellite" 
                    size="sm" 
                    color="purple" 
                    className="mr-3" 
                  />
                  <span className="text-muted-purple font-hebrew">
                    <MixedText>
                      {generating ? t('exams:studyGuide.generating', 'Generating study guide...') : t('exams:studyGuide.loading', 'Loading study guide...')}
                    </MixedText>
                  </span>
                </div>
              </div>
            </CardContent>
          </SpaceCard>
        </div>
      </PageContainer>
    );
  }

  // Show error state
  if (error || !studyGuide) {
    return (
      <PageContainer maxWidth="2xl" spacing="normal" backgroundIntensity="none" showParticles={false}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="max-w-md mx-auto space-y-6 text-center">
            {error && (
              <ErrorMessage fullScreen={false} />
            )}
            <div className={`flex gap-4 justify-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button
                onClick={() => navigate(`/course/${courseId}/exams`)}
                className="px-4 py-2 font-hebrew text-surface-secondary hover:text-vibrant-orange transition-colors border border-surface-border rounded-lg hover:border-vibrant-orange mr-3"
              >
                <MixedText>{t('common:actions.back_to_exams')}</MixedText>
              </button>
              <button
                onClick={async () => {
                  try {
                    setError(null);
                    setGenerating(false);
                    setLoading(true);
                    
                    // First, delete any existing study guide
                    await examApi.deleteCourseStudyGuide(parseInt(courseId!));
                    console.log('Previous study guide deleted, starting new generation');
                    
                    // Then retry the generation
                    loadStudyGuide();
                  } catch (deleteError: any) {
                    console.warn('Failed to delete previous study guide, proceeding anyway:', deleteError.message);
                    // Even if delete fails, try to regenerate
                    loadStudyGuide();
                  }
                }}
                className={`px-4 py-2 font-hebrew text-white bg-vibrant-orange hover:bg-vibrant-orange/80 transition-colors rounded-lg inline-flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
              >
                <RefreshCw className="w-4 h-4 mr-3 ml-3" />
                <span><MixedText>{t('common:actions.retry')}</MixedText></span>
              </button>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="2xl" spacing="normal" backgroundIntensity="none" showParticles={false}>
      {/* Header */}
      <div className="mb-8">
        {/* Breadcrumb Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/course/${courseId}/exams`)}
            className={`flex items-center text-surface-secondary hover:text-vibrant-orange transition-colors group font-hebrew ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
          >
            {isRTL ? (
              <ArrowLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200 ml-3 mr-3" />
            ) : (
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
            )}
            <span className={`font-medium ${isRTL ? 'text-right' : ''}`}>
              <MixedText>{t('common:actions.back_to_exams')}</MixedText>
            </span>
          </button>
        </div>

        {/* Study Guide Header */}
        <Card className="p-6 constellation-orion bg-gradient-to-br from-muted-purple/20 to-vibrant-orange/10">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-4' : 'space-x-4'}`}>
              <div className="w-16 h-16 bg-gradient-to-br from-muted-purple to-vibrant-orange rounded-2xl flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <div className={`${isRTL ? 'text-right' : ''}`}>
                <h1 className={`text-2xl font-bold text-surface-primary mb-1 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{t('exams:studyGuide.title', 'Study Guide')}</MixedText>
                </h1>
                <p className={`text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>
                    {isCourseLevel 
                      ? t('exams:studyGuide.courseGuideSubtitle', 'Based on all course exams')
                      : exam?.title || t('exams:studyGuide.subtitle', 'Exam preparation materials')
                    }
                  </MixedText>
                </p>
              </div>
            </div>

          </div>
        </Card>
      </div>

      {/* Simple Topic Navigation */}
      <div className="mb-8">
        <Card className="p-4 constellation-orion bg-gradient-to-br from-muted-purple/20 to-vibrant-orange/10">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Previous Topic Button */}
            <button
              onClick={() => {
                if (!studyGuide?.topics) return;
                const currentIndex = studyGuide.topics.findIndex(t => t.id === activeTopicTab) || 0;
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : studyGuide.topics.length - 1;
                switchTopic(studyGuide.topics[prevIndex].id);
              }}
              className={`p-2 rounded-lg border-2 border-surface-border bg-surface-subtle text-surface-secondary hover:border-vibrant-orange hover:bg-vibrant-orange/10 hover:text-vibrant-orange transition-all duration-200 ${isRTL ? 'rotate-180' : ''}`}
              title={t('exams:studyGuide.previousTopic', 'Previous Topic')}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Current Topic Display */}
            <div className={`flex items-center flex-1 mx-6 ${isRTL ? 'flex-row-reverse space-x-reverse space-x-3' : 'space-x-3'}`}>
              <div className="w-10 h-10 bg-vibrant-orange rounded-lg flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div className={`flex-1 min-w-0 text-center ${isRTL ? 'text-right' : ''}`}>
                <h3 className={`text-xl font-bold text-vibrant-orange font-hebrew ${isRTL ? 'text-right' : ''} truncate`}>
                  <MixedText>{currentTopic?.title || 'Loading...'}</MixedText>
                </h3>
                <p className={`text-sm text-surface-muted font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>
                    {currentTopic?.practice_questions?.length || 0} {t('exams:studyGuide.practiceQuestions', 'practice questions')}
                  </MixedText>
                </p>
              </div>
            </div>

            {/* Next Topic Button */}
            <button
              onClick={() => {
                if (!studyGuide?.topics) return;
                const currentIndex = studyGuide.topics.findIndex(t => t.id === activeTopicTab) || 0;
                const nextIndex = currentIndex < studyGuide.topics.length - 1 ? currentIndex + 1 : 0;
                switchTopic(studyGuide.topics[nextIndex].id);
              }}
              className={`p-2 rounded-lg border-2 border-surface-border bg-surface-subtle text-surface-secondary hover:border-vibrant-orange hover:bg-vibrant-orange/10 hover:text-vibrant-orange transition-all duration-200 ${isRTL ? 'rotate-180' : ''}`}
              title={t('exams:studyGuide.nextTopic', 'Next Topic')}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Topic Counter */}
          <div className={`text-center mt-3 text-sm text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
            <MixedText>
              {studyGuide?.topics ? (studyGuide.topics.findIndex(t => t.id === activeTopicTab) + 1) : 0} / {studyGuide?.topics?.length || 0} {t('exams:studyGuide.topics', 'Topics')}
            </MixedText>
          </div>
        </Card>
      </div>

      {/* Main Content - Interactive Journey */}
      {currentTopic && (
        <div className="space-y-6">
          {/* Learning Guide Section */}
          <Card className="constellation-orion">
            <CardHeader>
              <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-3' : 'space-x-3'}`}>
                <div className="w-10 h-10 bg-vibrant-orange rounded-lg flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <CardTitle className={`text-2xl font-bold text-vibrant-orange flex-1 min-w-0 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{currentTopic.title}</MixedText>
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className={`prose prose-lg max-w-none ${isRTL ? 'text-right' : ''}`}>
                <StudyMaterialRenderer
                  content={currentTopic.learning_guide}
                  materialType="study_guide"
                  isRTL={isRTL}
                />
              </div>
            </CardContent>
          </Card>

          {/* Practice Questions Section */}
          {currentTopic.practice_questions && currentTopic.practice_questions.length > 0 && (
            <div className="space-y-6">
              {/* Individual Question Cards */}
              {currentTopic.practice_questions.map((question, index) => (
                <Card key={question.id} className="constellation-cassiopeia transition-all duration-300">
                  <CardHeader>
                    <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <CardTitle className={`text-surface-primary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                        <MixedText>{`${t('exams:studyGuide.practiceQuestion', 'Practice Question')} ${index + 1}`}</MixedText>
                      </CardTitle>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    {/* Question Text */}
                    <div className={`prose prose-lg max-w-none mb-6 ${isRTL ? 'text-right' : ''}`}>
                      <StudyMaterialRenderer
                        content={question.question_text}
                        materialType="question"
                        isRTL={isRTL}
                      />
                    </div>

                    {/* Solution Toggle Button */}
                    <div className="mb-4">
                      <button
                        onClick={() => toggleSolution(question.id)}
                        className={`w-full p-3 border-2 border-dashed rounded-lg transition-all duration-200 hover:scale-[1.01] text-center ${
                          showSolutions[question.id]
                            ? 'border-muted-purple/50 bg-muted-purple/10 text-muted-purple hover:bg-muted-purple/20'
                            : 'border-vibrant-orange/50 bg-vibrant-orange/5 text-vibrant-orange hover:bg-vibrant-orange/10'
                        }`}
                      >
                        <div className={`flex items-center justify-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-2' : 'space-x-2'}`}>
                          <Trophy className={`w-4 h-4 ${showSolutions[question.id] ? 'text-muted-purple' : 'text-vibrant-orange'}`} />
                          <span className="font-medium text-sm">
                            <MixedText>
                              {showSolutions[question.id] 
                                ? t('exams:studyGuide.hideSolution', 'Hide Solution')
                                : t('exams:studyGuide.showSolution', 'Show Solution')
                              }
                            </MixedText>
                          </span>
                        </div>
                      </button>
                    </div>

                    {/* Collapsible Solution */}
                    {showSolutions[question.id] && (
                      <div className="mt-4 p-4 bg-gradient-to-br from-muted-purple/10 to-muted-purple/5 border-2 border-muted-purple/20 rounded-xl animate-fadeIn">
                        <h4 className={`font-semibold text-muted-purple mb-3 font-hebrew text-base ${isRTL ? 'text-right' : ''}`}>
                          <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-2' : 'space-x-2'}`}>
                            <Trophy className="w-4 h-4" />
                            <span><MixedText>{t('exams:studyGuide.solution', 'Solution')}</MixedText></span>
                          </div>
                        </h4>
                        <div className={`text-surface-primary ${isRTL ? 'text-right' : ''}`}>
                          <StudyMaterialRenderer
                            content={question.solution_text}
                            materialType="solution"
                            isRTL={isRTL}
                          />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Regenerate Study Guide Button */}
          <div className="mt-8 flex justify-center">
            <button
              onClick={async () => {
                try {
                  setError(null);
                  setGenerating(false);
                  setLoading(true);
                  
                  // First, delete any existing study guide
                  await examApi.deleteCourseStudyGuide(parseInt(courseId!));
                  console.log('Previous study guide deleted, starting new generation');
                  
                  // Then retry the generation
                  loadStudyGuide();
                } catch (deleteError: any) {
                  console.warn('Failed to delete previous study guide, proceeding anyway:', deleteError.message);
                  // Even if delete fails, try to regenerate
                  loadStudyGuide();
                }
              }}
              className={`px-6 py-3 flex items-center border-2 border-vibrant-orange/30 bg-vibrant-orange/10 hover:bg-vibrant-orange/20 text-vibrant-orange hover:text-vibrant-orange rounded-lg transition-all duration-200 font-hebrew ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
            >
              <RefreshCw className="w-4 h-4 mr-3 ml-3" />
              <span>
                <MixedText>{t('exams:studyGuide.regenerateStudyGuide', 'Regenerate Study Guide')}</MixedText>
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Floating Chat Widget - show if study guide is loaded */}
      {studyGuide && (
        <>
          <ChatWidget
            sessionId={parseInt(courseId!)} // Use courseId as session identifier
            isOpen={isChatOpen}
            onClose={() => {
              setIsChatOpen(false);
              // Clear feedback state when chat is closed
              setMessageFeedback({});
              // Clear pending message when chat is closed
              setPendingMessage(null);
            }}
            chatHistory={chatHistory}
            onChatUpdate={handleChatUpdate}
            messageFeedback={messageFeedback}
            onMessageFeedback={handleMessageFeedback}
            isRTL={isRTL}
            customChatHandler={studyGuideChatHandler}
            pendingMessage={pendingMessage}
            onPendingMessageHandled={() => setPendingMessage(null)}
            onMinimizedStateChange={setIsChatMinimized}
            unminimizeChat={shouldUnminimizeChat}
            enableModeSwitch={false}
          />

          {/* Chat Toggle Button */}
          <button
            onClick={handleChatToggle}
            className="fixed bottom-4 md:bottom-6 right-4 md:right-6 z-[60] bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 hover:from-vibrant-orange/90 hover:to-vibrant-orange/80 text-white px-3 md:px-4 py-2.5 md:py-3 rounded-full shadow-lg shadow-vibrant-orange/30 transition-all duration-200 hover:scale-105 flex items-center gap-2 md:gap-3"
            title={t('exams:studyGuide.chat.openChat', 'Open Study Guide Chat')}
          >
            <img
              src="/logo_v2.png"
              alt="StudyBel Astronaut"
              className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-white/10 p-1"
            />
            <span className="font-medium text-xs md:text-sm whitespace-nowrap hidden sm:inline">
              <MixedText>{t('exams:studyGuide.chat.askQuestion', 'Ask Question')}</MixedText>
            </span>
          </button>
        </>
      )}
    </PageContainer>
  );
};

export default ExamStudyGuideView;
